import { useState } from "react";
import { CheckCircle2, Loader2, Paperclip, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { trackEvent, getConsent } from "@/lib/analytics";
import { utmSuffix, getUtm } from "@/lib/utm";
import { EMIRATES, PROPERTY_TYPES } from "@/lib/contact";

const PROJECT_STAGES = ["Under construction", "Fit-out or renovation", "Completed property"];
const FILE_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";
const FILE_MAX_MB = 10;

const EMPTY = {
  name: "", email: "", phone: "", emirate: "", community: "", property_type: "",
  size: "", project_stage: "", preferred_date: "", access_confirmed: "",
  report_available: "No", message: "",
  privacy_consent: false, terms_accepted: false, marketing_consent: false,
  website: "", // honeypot — humans never see or fill this field
};

/**
 * Embedded enquiry form for the dedicated service pages (defect cost
 * estimation / site inspection). Posts multipart to /api/service-enquiries so
 * an optional inspection report / photo upload travels with the same request.
 */
export default function ServiceEnquiryForm({ config }) {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((errs) => (errs[k] ? { ...errs, [k]: undefined } : errs));
  };
  const field = "w-full bg-white border border-line rounded-lg px-3.5 py-2.5 text-base sm:text-sm text-ink focus:border-blue focus:ring-2 focus:ring-blue/15 outline-none transition";

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    if (!f) { setFile(null); return; }
    const ext = `.${(f.name.split(".").pop() || "").toLowerCase()}`;
    if (!FILE_ACCEPT.split(",").includes(ext)) {
      toast.error("Report must be a PDF or image (.pdf, .jpg, .jpeg, .png, .webp).");
      e.target.value = "";
      setFile(null);
      return;
    }
    if (f.size > FILE_MAX_MB * 1024 * 1024) {
      toast.error(`File must be smaller than ${FILE_MAX_MB} MB.`);
      e.target.value = "";
      setFile(null);
      return;
    }
    setFile(f);
  };

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your full name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    if (!form.phone.trim()) errs.phone = "Please enter your phone / WhatsApp number.";
    if (!form.emirate) errs.emirate = "Please choose an emirate.";
    if (!form.community.trim()) errs.community = "Please enter the community or project.";
    if (!form.property_type) errs.property_type = "Please choose a property type.";
    if (config.projectStage && !form.project_stage) errs.project_stage = "Please choose the project stage.";
    if (!form.privacy_consent) errs.privacy_consent = "Please accept the privacy policy to continue.";
    if (!form.terms_accepted) errs.terms_accepted = "Please read and agree to the Terms and Conditions to continue.";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      const first = document.querySelector("[data-invalid='true']");
      first?.focus();
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("service", config.serviceKey);
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("emirate", form.emirate);
      fd.append("community", form.community);
      fd.append("property_type", form.property_type);
      fd.append("size", form.size);
      fd.append("project_stage", form.project_stage);
      fd.append("preferred_date", form.preferred_date);
      fd.append("access_confirmed", form.access_confirmed);
      fd.append("report_available", form.report_available);
      fd.append("message", form.message);
      fd.append("privacy_consent", form.privacy_consent ? "true" : "false");
      fd.append("terms_accepted", form.terms_accepted ? "true" : "false");
      fd.append("marketing_consent", form.marketing_consent ? "true" : "false");
      fd.append("terms_version", "1.1");
      fd.append("terms_effective_date", "2026-09-25");
      fd.append("website", form.website);
      fd.append("source", `${window.location.pathname}${window.location.search || utmSuffix()}`);
      fd.append("attribution", JSON.stringify({ ...getUtm(), consent_status: getConsent() || "unknown" }));
      if (file) fd.append("report", file);
      await api.post("/service-enquiries", fd);
      trackEvent("quote_submit", { service: config.serviceName, property_type: form.property_type, emirate: form.emirate, conversion_value: 1.0 });
      trackEvent("generate_lead", { service: config.serviceName, property_type: form.property_type, emirate: form.emirate, source_path: window.location.pathname });
      setDone(true);
    } catch (err) {
      trackEvent("quote_error", { service: config.serviceName });
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not send request.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="service-enquiry-success">
        <div className="w-16 h-16 rounded-full bg-blue-50 text-blue grid place-items-center mx-auto mb-5"><CheckCircle2 size={32} /></div>
        <h3 className="text-2xl mb-2">Thank you, {form.name.split(" ")[0] || "there"}.</h3>
        <p className="text-muted max-w-[46ch] mx-auto">We have received your <b className="text-ink">{config.serviceName}</b> enquiry for your {form.property_type.toLowerCase()} in {form.emirate}. Fedari will review the details and contact you at <b className="text-ink">{form.email}</b> or on your phone / WhatsApp number.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="service-enquiry-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <L label="Full name *" error={errors.name}>
          <input className={field} data-invalid={!!errors.name || undefined} value={form.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" data-testid="svc-name" />
        </L>
        <L label="Email address *" error={errors.email}>
          <input type="email" className={field} data-invalid={!!errors.email || undefined} value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" data-testid="svc-email" />
        </L>
        <L label="Phone / WhatsApp number *" error={errors.phone}>
          <input type="tel" className={field} data-invalid={!!errors.phone || undefined} value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" placeholder="e.g. +971 50 000 0000" data-testid="svc-phone" />
        </L>
        <L label="Emirate *" error={errors.emirate}>
          <select className={field} data-invalid={!!errors.emirate || undefined} value={form.emirate} onChange={(e) => set("emirate", e.target.value)} data-testid="svc-emirate">
            <option value="" disabled>Select emirate…</option>
            {EMIRATES.map((o) => <option key={o}>{o}</option>)}
          </select>
        </L>
        <L label="Community / project *" error={errors.community}>
          <input className={field} data-invalid={!!errors.community || undefined} value={form.community} onChange={(e) => set("community", e.target.value)} placeholder="e.g. Dubai Hills" data-testid="svc-community" />
        </L>
        <L label="Property type *" error={errors.property_type}>
          <select className={field} data-invalid={!!errors.property_type || undefined} value={form.property_type} onChange={(e) => set("property_type", e.target.value)} data-testid="svc-property-type">
            <option value="" disabled>Select property type…</option>
            {PROPERTY_TYPES.map((o) => <option key={o}>{o}</option>)}
          </select>
        </L>
        <L label="Property size — sq. ft. (optional)">
          <input type="number" min="1" inputMode="numeric" className={field} value={form.size} onChange={(e) => set("size", e.target.value)} placeholder="e.g. 1200" data-testid="svc-size" />
        </L>
        {config.projectStage ? (
          <L label="Project stage *" error={errors.project_stage}>
            <select className={field} data-invalid={!!errors.project_stage || undefined} value={form.project_stage} onChange={(e) => set("project_stage", e.target.value)} data-testid="svc-project-stage">
              <option value="" disabled>Select project stage…</option>
              {PROJECT_STAGES.map((o) => <option key={o}>{o}</option>)}
            </select>
          </L>
        ) : (
          <L label="Existing inspection report available?">
            <select className={field} value={form.report_available} onChange={(e) => set("report_available", e.target.value)} data-testid="svc-report-available">
              <option>No</option>
              <option>Yes</option>
            </select>
          </L>
        )}
        {config.projectStage && (
          <>
            <L label="Preferred inspection date">
              <input type="date" className={field} value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} data-testid="svc-date" />
            </L>
            <L label="Access confirmed?">
              <select className={field} value={form.access_confirmed} onChange={(e) => set("access_confirmed", e.target.value)} data-testid="svc-access">
                <option value="">Not sure yet</option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </L>
          </>
        )}
      </div>

      {config.upload && (
        <div>
          <label htmlFor="svc-report-file" className="flex items-center gap-2.5 border border-dashed border-line rounded-lg px-4 py-3.5 text-sm text-bodytext cursor-pointer hover:border-blue transition-colors" data-testid="svc-upload-label">
            <Paperclip size={16} className="text-blue flex-none" />
            <span>{file ? file.name : "Upload inspection report or photographs (optional — PDF or image, max 10 MB)"}</span>
          </label>
          <input id="svc-report-file" type="file" accept={FILE_ACCEPT} onChange={onFile} className="sr-only" data-testid="svc-report-file" />
        </div>
      )}

      <L label="Additional details">
        <textarea rows={4} className={field} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Anything we should know about the property or the defects?" data-testid="svc-message" />
      </L>

      {/* Honeypot — visually hidden, bots only */}
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute opacity-0 pointer-events-none h-0 w-0" value={form.website} onChange={(e) => set("website", e.target.value)} />

      <div className="space-y-2.5 pt-1">
        <label className="flex items-start gap-2.5 text-sm text-bodytext py-1">
          <input type="checkbox" checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} data-invalid={!!errors.privacy_consent || undefined} className="h-5 w-5 mt-0.5 accent-blue" data-testid="svc-privacy-consent" />
          <span>I agree that Fedari may process my details to handle my enquiry, as described in the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue font-semibold underline">Privacy Policy</a>. *</span>
        </label>
        {errors.privacy_consent && <p className="text-xs text-sev-high -mt-1">{errors.privacy_consent}</p>}
        <label className="flex items-start gap-2.5 text-sm text-bodytext py-1">
          <input type="checkbox" checked={form.terms_accepted} onChange={(e) => set("terms_accepted", e.target.checked)} data-invalid={!!errors.terms_accepted || undefined} className="h-5 w-5 mt-0.5 accent-blue" data-testid="svc-terms-accept" />
          <span>I confirm that I have read and agree to Fedari&apos;s <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-blue font-semibold underline" data-testid="svc-terms-link">Terms and Conditions</a>, Version 1.1, effective 25 September 2026. *</span>
        </label>
        {errors.terms_accepted && <p className="text-xs text-sev-high -mt-1">{errors.terms_accepted}</p>}
        <label className="flex items-start gap-2.5 text-sm text-muted py-1">
          <input type="checkbox" checked={form.marketing_consent} onChange={(e) => set("marketing_consent", e.target.checked)} className="h-5 w-5 mt-0.5 accent-blue" data-testid="svc-marketing-consent" />
          <span>Send me occasional inspection tips and offers (optional).</span>
        </label>
      </div>

      <button type="submit" disabled={busy} data-testid="svc-submit"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-blue text-white px-8 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors disabled:opacity-60 min-h-[44px]">
        {busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />} {config.cta}
      </button>
    </form>
  );
}

function L({ label, error, children }) {
  return (
    <div>
      <span className="block text-sm font-medium text-ink mb-1.5">{label}</span>
      {children}
      {error && <p className="text-xs text-sev-high mt-1">{error}</p>}
    </div>
  );
}
